import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AddCircle } from 'iconsax-react';
import { MoreHoriz } from '@mui/icons-material';
import { Iconly } from 'react-iconly';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { StyledMenu } from '@ui/components/common/Menu';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import MetricChip from '@ui/components/common/MetricChip';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import VehiclesNewVehicleDialog from './VehiclesNewVehicleDialog';
import VehicleAttendeesDialog from './VehicleAttendeesDialog';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { formatDMY } from '@application/utilities/date-time-formater';
import { getEventVehiclesByEventIdAsync, createEventVehicleAsync, updateEventVehicleAsync, deleteEventVehicleAsync, getAttendeesByRouteVehicleIdAsync, assignAttendeeToRouteVehicleAsync, sendVehicleInformationToSupplierAsync, exportBirthdayInfoToExcelAsync, getRouteVehiclesByPickupLocationAsync } from '@application/features/eventVehicle/eventVehicleThunk';
const VehiclesContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { vehicles } = useAppSelector((state) => state.eventVehicle);
    const event = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const isFamilyFest = event?.type === EventTypeEnum.Family;
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    const hideForSfeermeester = isFamilyFest && isSfeermeester;
    // Vehicles are scoped by the selected companies via the auth token,
    // so the list must be refetched whenever the sidebar company selection changes.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [searchValue, setSearchValue] = useState('');
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [openAttendeesDialog, setOpenAttendeesDialog] = useState(false);
    const [selectedAttendeesRows, setSelectedAttendeesRows] = useState([]);
    const [attendeesLoading, setAttendeesLoading] = useState(false);
    const [currentRouteVehicleId, setCurrentRouteVehicleId] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filters, setFilters] = useState({});
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    useEffect(() => {
        if (eventId) {
            const sort = sortModel[0];
            dispatch(getEventVehiclesByEventIdAsync({
                eventId,
                query: {
                    eventId: eventId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    sortBy: sort?.field,
                    sortOrder: sort?.sort ?? 'desc',
                    ...filters
                }
            }));
        }
    }, [dispatch, eventId, paginationModel, sortModel, searchValue, filters, selectedCompanyIds]);
    const supplierOptions = useMemo(() => (event?.assignedCollaborators ?? []).map((collaborator) => ({
        label: `${collaborator.firstName} ${collaborator.lastName}`.trim(),
        value: collaborator.id
    })), [event?.assignedCollaborators]);
    const vehicleRows = useMemo(() => (vehicles?.items ?? []).map((v) => ({ ...v, attendeesRows: [] })), [vehicles]);
    const filteredVehicles = useMemo(() => vehicleRows, [vehicleRows]);
    const handleOpenRowMenu = (event, vehicle) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedVehicle(vehicle);
    };
    const handleCloseRowMenu = () => {
        setMenuAnchorEl(null);
        setSelectedVehicle(null);
    };
    const handleEditVehicle = useCallback(() => {
        if (!selectedVehicle)
            return;
        setEditingVehicle(selectedVehicle);
        setOpenCreateDialog(true);
        handleCloseRowMenu();
    }, [selectedVehicle]);
    const handleDeleteVehicle = useCallback(() => {
        if (!selectedVehicle)
            return;
        setVehicleToDelete(selectedVehicle);
        setDeleteConfirmOpen(true);
        handleCloseRowMenu();
    }, [selectedVehicle]);
    const handleConfirmDelete = async () => {
        if (!vehicleToDelete)
            return;
        try {
            await dispatch(deleteEventVehicleAsync(vehicleToDelete.id)).unwrap();
            toast.success(t('events.eventDetails.transportTab.vehicleDeletedSuccessfully'));
            if (eventId) {
                dispatch(getEventVehiclesByEventIdAsync({
                    eventId,
                    query: {
                        eventId: eventId,
                        pageNumber: 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined,
                        ...filters
                    }
                }));
            }
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.transportTab.vehicleDeleteFailed'));
        }
        finally {
            setDeleteConfirmOpen(false);
            setVehicleToDelete(null);
        }
    };
    const handleSendToCarrier = useCallback(async () => {
        if (!selectedVehicle)
            return;
        try {
            await dispatch(sendVehicleInformationToSupplierAsync(selectedVehicle.id)).unwrap();
            toast.success(t('events.eventDetails.transportTab.informationSentToCarrierSuccessfully'));
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.transportTab.informationSendFailed'));
        }
        finally {
            handleCloseRowMenu();
        }
    }, [selectedVehicle, dispatch, t]);
    const handleExportBirthdayInfo = useCallback(async () => {
        if (!selectedVehicle)
            return;
        try {
            const result = await dispatch(exportBirthdayInfoToExcelAsync(selectedVehicle.id)).unwrap();
            if (result) {
                const url = window.URL.createObjectURL(result);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Birthdays_${selectedVehicle.vehicleNumber}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success(t('events.eventDetails.transportTab.birthdayInfoExportedSuccessfully'));
            }
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.transportTab.birthdayInfoExportFailed'));
        }
        finally {
            handleCloseRowMenu();
        }
    }, [selectedVehicle, dispatch, t]);
    const rowActions = useMemo(() => {
        const actions = [
            ...(!hideForSfeermeester
                ? [
                    {
                        text: t('actions.edit'),
                        icon: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }),
                        to: handleEditVehicle,
                        group: true
                    }
                ]
                : [])
        ];
        if (selectedVehicle?.supplierId && isFamilyFest) {
            actions.push({
                text: t('events.eventDetails.transportTab.sendToCarrier'),
                icon: _jsx(Iconly, { name: 'Send', set: 'curved' }),
                to: handleSendToCarrier,
                group: true
            });
        }
        if (isFamilyFest) {
            actions.push({
                text: t('events.eventDetails.transportTab.sendBirthdayInfo'),
                icon: _jsx(Iconly, { name: 'Document', set: 'curved' }),
                to: handleExportBirthdayInfo,
                group: true
            });
        }
        if (!hideForSfeermeester) {
            actions.push({
                text: t('actions.delete'),
                icon: _jsx(Iconly, { name: 'Delete', set: 'curved' }),
                color: 'error.light',
                to: handleDeleteVehicle,
                group: true
            });
        }
        return actions;
    }, [t, theme.palette.text.primary, handleEditVehicle, handleDeleteVehicle, handleSendToCarrier, handleExportBirthdayInfo, selectedVehicle, isFamilyFest, hideForSfeermeester]);
    const columns = [
        {
            field: 'vehicleName',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.vehicleName'),
            flex: 1.2,
            minWidth: 180,
            renderCell: (params) => params.row.vehicleName || '-'
        },
        {
            field: 'vehicleNumber',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.vehicleNumber'),
            flex: 1.2,
            minWidth: 180
        },
        {
            field: 'seatingCapacity',
            headerName: t('events.eventDetails.transportTab.seatingCapacity'),
            flex: 0.8,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                const consumed = Number(params.row.assignedCount ?? 0);
                const totalCapacity = Number(params.row.totalCapacity ?? 0);
                return `${consumed}/${totalCapacity}`;
            }
        },
        {
            field: 'assignedCount',
            headerName: t('events.eventDetails.transportTab.attendees'),
            flex: 0.8,
            minWidth: 140,
            renderCell: (params) => (_jsx(Box, { onClick: async (event) => {
                    event.stopPropagation();
                    const routeVehicleId = params.row.routeVehicleId ?? null;
                    setCurrentRouteVehicleId(routeVehicleId);
                    if (!routeVehicleId) {
                        setSelectedAttendeesRows([]);
                        setOpenAttendeesDialog(true);
                        return;
                    }
                    setAttendeesLoading(true);
                    setOpenAttendeesDialog(true);
                    try {
                        const result = await dispatch(getAttendeesByRouteVehicleIdAsync(routeVehicleId)).unwrap();
                        const rows = result.map((a) => ({
                            id: a.id,
                            name: a.name,
                            company: a.companyName,
                            type: a.attendeeType ?? '',
                            primaryPerson: a.primaryAttendeeName || '-',
                            registered: formatDMY(a.createdDate),
                            isBirthday: a.hasBirthdayOnEventDate,
                            pickupLocationId: a.pickupLocationId
                        }));
                        setSelectedAttendeesRows(rows);
                    }
                    catch {
                        setSelectedAttendeesRows([]);
                    }
                    finally {
                        setAttendeesLoading(false);
                    }
                }, sx: { cursor: 'pointer', display: 'inline-flex' }, children: _jsx(MetricChip, { value: params.row.assignedCount }) }))
        },
        {
            field: 'supplierName',
            headerName: t('events.eventDetails.transportTab.carrierName'),
            flex: 1,
            minWidth: 180,
            renderCell: (params) => params.row.supplierName || '-'
        },
        {
            field: 'subcontractorName',
            headerName: t('events.eventDetails.transportTab.subcontractorName'),
            flex: 1,
            minWidth: 180,
            renderCell: (params) => params.row.subcontractorName || '-'
        },
        {
            field: 'driverNumber',
            headerName: t('events.eventDetails.transportTab.driverNumber'),
            type: 'string',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'wheelchairSpots',
            headerName: t('events.eventDetails.transportTab.wheelChair'),
            flex: 0.8,
            minWidth: 140,
            renderCell: (params) => params.row.wheelchairSpots
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            minWidth: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (_jsx(IconButton, { color: 'inherit', onClick: (event) => handleOpenRowMenu(event, params.row), children: _jsx(MoreHoriz, {}) }))
        }
    ];
    const handleCreateOrEditVehicle = async (values) => {
        if (!eventId)
            return;
        if (editingVehicle) {
            try {
                await dispatch(updateEventVehicleAsync({
                    id: editingVehicle.id,
                    vehicleName: values.vehicleName.trim(),
                    vehicleNumber: values.vehicleNumber,
                    subcontractorName: values.subcontractorName?.trim() || null,
                    carrierName: values.carrierName,
                    supplierId: values.supplierId,
                    driverNumber: values.driverNumber || null,
                    totalCapacity: Number(values.totalSeats),
                    wheelchairSpots: Number(values.wheelchairSpots)
                })).unwrap();
                toast.success(t('events.eventDetails.transportTab.vehicleUpdatedSuccessfully'));
                dispatch(getEventVehiclesByEventIdAsync({
                    eventId,
                    query: {
                        eventId: eventId,
                        pageNumber: paginationModel.page + 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined,
                        ...filters
                    }
                }));
            }
            catch (error) {
                toast.error(error?.error || t('events.eventDetails.transportTab.vehicleUpdateFailed'));
                return;
            }
        }
        else {
            try {
                await dispatch(createEventVehicleAsync({
                    eventId,
                    vehicleName: values.vehicleName.trim(),
                    vehicleNumber: values.vehicleNumber,
                    subcontractorName: values.subcontractorName?.trim() || null,
                    carrierName: values.carrierName,
                    supplierId: values.supplierId,
                    driverNumber: values.driverNumber || null,
                    totalCapacity: Number(values.totalSeats),
                    wheelchairSpots: Number(values.wheelchairSpots)
                })).unwrap();
                toast.success(t('events.eventDetails.transportTab.vehicleCreatedSuccessfully'));
                dispatch(getEventVehiclesByEventIdAsync({
                    eventId,
                    query: {
                        eventId: eventId,
                        pageNumber: 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined,
                        ...filters
                    }
                }));
            }
            catch (error) {
                toast.error(error?.error || t('events.eventDetails.transportTab.vehicleCreationFailed'));
                return;
            }
        }
        setEditingVehicle(null);
        setOpenCreateDialog(false);
    };
    const moveToVehicleOptions = useMemo(() => vehicleRows.map((vehicle) => ({
        id: vehicle.id,
        routeVehicleId: vehicle.routeVehicleId ?? null,
        vehicleNumber: vehicle.vehicleNumber,
        seatingCapacity: {
            consumed: vehicle.assignedCount,
            total: vehicle.totalCapacity
        }
    })), [vehicleRows]);
    const refreshAttendees = useCallback(async (routeVehicleId) => {
        const result = await dispatch(getAttendeesByRouteVehicleIdAsync(routeVehicleId)).unwrap();
        const rows = result.map((a) => ({
            id: a.id,
            name: a.name,
            company: a.companyName,
            type: a.attendeeType ?? '',
            primaryPerson: a.primaryAttendeeName || '-',
            registered: formatDMY(a.createdDate),
            isBirthday: a.hasBirthdayOnEventDate,
            pickupLocationId: a.pickupLocationId
        }));
        setSelectedAttendeesRows(rows);
        if (eventId) {
            dispatch(getEventVehiclesByEventIdAsync({
                eventId,
                query: {
                    eventId: eventId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    ...filters
                }
            }));
        }
    }, [dispatch, eventId, paginationModel, searchValue, filters]);
    const handleFetchVehiclesForAttendee = useCallback(async (pickupLocationId) => {
        const result = await dispatch(getRouteVehiclesByPickupLocationAsync(pickupLocationId)).unwrap();
        return result.map((v) => ({
            id: v.id,
            routeVehicleId: v.routeVehicleId ?? null,
            vehicleNumber: v.vehicleNumber,
            seatingCapacity: { consumed: v.assignedCount, total: v.totalCapacity }
        }));
    }, [dispatch]);
    const handleMoveToVehicle = useCallback(async (assignmentId, targetRouteVehicleId) => {
        try {
            await dispatch(assignAttendeeToRouteVehicleAsync({ assignmentIds: [assignmentId], routeVehicleId: targetRouteVehicleId })).unwrap();
            toast.success(t('events.eventDetails.transportTab.attendeeMovedSuccessfully'));
            if (currentRouteVehicleId)
                await refreshAttendees(currentRouteVehicleId);
        }
        catch (error) {
            toast.error(error?.error || t('common.somethingWentWrong'));
        }
    }, [currentRouteVehicleId, dispatch, refreshAttendees, t]);
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: filteredVehicles, showHeaderControls: false, showFilters: true, onApplyFilters: (newFilters) => {
                    const mappedFilters = { ...newFilters };
                    if (newFilters.supplierName) {
                        mappedFilters.supplierId = newFilters.supplierName;
                        delete mappedFilters.supplierName;
                    }
                    if (newFilters.seatingCapacity) {
                        mappedFilters.totalCapacity = newFilters.seatingCapacity;
                        delete mappedFilters.seatingCapacity;
                    }
                    setFilters(mappedFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, resetFiltersKey: resetFiltersKey, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, filterOptions: {
                    supplierName: supplierOptions,
                }, paginationMode: 'server', rowCount: vehicles?.totalCount || 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, showNewHeaderControls: true, noRowsText: t('events.eventDetails.transportTab.noVehiclesFound'), maxHeight: 'calc(100dvh - 240px)', customButton: _jsxs(Box, { display: 'flex', flexDirection: isSmallScreen ? 'column' : 'row', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', gap: isSmallScreen ? 1.5 : 0, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsxs(Typography, { variant: 'h4', children: [vehicles?.totalCount || 0, " ", t('events.eventDetails.transportTab.vehicles')] }), isSmallScreen && !hideForSfeermeester && (_jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: 'small', color: 'primary', onClick: () => setOpenCreateDialog(true), children: t('events.eventDetails.transportTab.newVehicle') }))] }), _jsxs(Box, { display: 'flex', gap: 1.5, children: [_jsx(SearchFilter, { forceDesktopVariant: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('events.eventDetails.transportTab.searchVehicle'), onClear: () => setSearchValue(''), onFilterClick: () => setGridFilterOpen(true), showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                        setFilters({});
                                        setResetFiltersKey((prev) => prev + 1);
                                    } }), !isSmallScreen && !hideForSfeermeester && (_jsx(Button, { startIcon: _jsx(AddCircle, {}), variant: 'contained', size: 'large', color: 'primary', onClick: () => setOpenCreateDialog(true), children: t('events.eventDetails.transportTab.newVehicle') }))] })] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(VehiclesNewVehicleDialog, { open: openCreateDialog, onClose: () => {
                    setOpenCreateDialog(false);
                    setEditingVehicle(null);
                }, editingVehicle: editingVehicle
                    ? {
                        id: editingVehicle.id,
                        assignedCount: editingVehicle.assignedCount ?? 0,
                        vehicleName: editingVehicle.vehicleName ?? '',
                        vehicleNumber: editingVehicle.vehicleNumber,
                        subcontractorName: editingVehicle.subcontractorName ?? '',
                        totalSeats: editingVehicle.totalCapacity,
                        wheelchairSpots: editingVehicle.wheelchairSpots,
                        carrierName: editingVehicle.carrierName,
                        supplierId: editingVehicle.supplierId || null,
                        driverNumber: editingVehicle.driverNumber ?? ''
                    }
                    : null, onSubmit: handleCreateOrEditVehicle }), _jsx(VehicleAttendeesDialog, { open: openAttendeesDialog, loading: attendeesLoading, onClose: () => {
                    setOpenAttendeesDialog(false);
                    setSelectedAttendeesRows([]);
                    setCurrentRouteVehicleId(null);
                }, rows: selectedAttendeesRows, moveToVehicleOptions: moveToVehicleOptions, onMoveToVehicle: handleMoveToVehicle, isFamilyFest: isFamilyFest, onFetchVehiclesForAttendee: isFamilyFest ? handleFetchVehiclesForAttendee : undefined }), _jsx(ConfirmationDialog, { open: deleteConfirmOpen, onClose: () => {
                    setDeleteConfirmOpen(false);
                    setVehicleToDelete(null);
                }, title: t('events.eventDetails.transportTab.deleteVehicle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: () => {
                                setDeleteConfirmOpen(false);
                                setVehicleToDelete(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', size: 'large', fullWidth: true, onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { children: t('events.eventDetails.transportTab.deleteVehicleConfirmation', {
                            vehicleNumber: vehicleToDelete?.vehicleNumber
                        }) }) }) }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleCloseRowMenu, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, actions: rowActions })] }));
};
export default VehiclesContent;
